import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import FormRequest from '../common/formRequest';
import { FormBuilder, Validators } from '@angular/forms';
import { ApplicantSEADTypeEnum } from 'src/app/Enums/applicantSEADTypeEnum';
import { AutoridadeEnum } from 'src/app/Enums/autoridadeTypeEnum';
import { FiscoService } from 'src/app/app-core/services/fisco.service';
import { LocalStorageService } from 'src/app/app-core/services/local-storage.service';
import { EnderecoFormComponent } from 'src/app/components/endereco-form/endereco-form.component';

@Component({
  selector: 'app-form-request-sead',
  templateUrl: './form-request-sead.component.html',
  styleUrls: ['./form-request-sead.component.scss'],
  providers: [LocalStorageService]
})
export class FormRequestSeadComponent extends FormRequest implements OnInit, AfterViewInit {

  @ViewChild(EnderecoFormComponent) enderecoFormComponent: EnderecoFormComponent;

  private localStorageKey = 'form-request-sead';
  private draftRestored = false;

  public checkBoxInputs: { label: string, value: string }[] = [
    {
      label: 'SERVIDOR(A)',
      value: ApplicantSEADTypeEnum.SERVER
    },
    {
      label: 'BENEFICIÁRIO(A)',
      value: ApplicantSEADTypeEnum.RECIPIENT
    },
  ];

  private requiredFieldsForServer = [
    "SOLS_RequerenteRG",
    "SOLS_RequerenteRGOrgaoExpeditor",
    "SOLS_RequerenteTelefone",
    "SOLS_RequerenteDataNascimento",
    "SOLS_RequerenteCargo",
    "SOLS_RequerenteLotacao",
  ];

  private requiredFieldsForRecipient = [
    "SOLS_RequerenteNome",
    "SOLS_RequerenteCPF",
    "SOLS_RequerenteParentescoExServidor",
  ];

  public autoridades: { label: string, value: string }[] = [
    {
      label: "Secretário(a) de Infraestrutura",
      value: AutoridadeEnum.SECRETARIO_INFRAESTRUTURA
    },
    {
      label: "Secretário(a) de Educação",
      value: AutoridadeEnum.SECRETARIO_EDUCACAO
    },
    {
      label: "Secretário(a) Regional Pecém",
      value: AutoridadeEnum.SECRETARIO_REGEGIONAL_PECEM,

    },
    {
      label: "Prefeito Municipal",
      value: AutoridadeEnum.PREFEITO_MUNICIPAL
    },
    {
      label: "Ouvidor(a)",
      value: AutoridadeEnum.OUVIDOR
    },
    {
      label: "Secretário(a) de Saúde",
      value: AutoridadeEnum.SECRETARIO_SAUDE
    },
    {
      label: "Controlador(a)",
      value: AutoridadeEnum.CONTROLADOR
    },
    {
      label: "Secretário(a) de Administração",
      value: AutoridadeEnum.SECRETARIO_ADMINISTRACAO
    },
    {
      label: "Secretário(a) de Governo",
      value: AutoridadeEnum.SECRETARIO_GOVERNO
    },
    {
      label: "Secretário(a) de Trab. e Des.Social",
      value: AutoridadeEnum.SECRETARIO_TRABALHO_SOCIAL
    },
    {
      label: "Procurador(a) Geral",
      value: AutoridadeEnum.PROCURADOR_GERAL
    },
    {
      label: "Secretário(a) Geral",
      value: AutoridadeEnum.SECRETARIO_GERAL
    },
    {
      label: "Secretário(a) de Finanças",
      value: AutoridadeEnum.SECRETARIO_FINANCAS
    },
    {
      label: "Coordenador(a) do FMPS",
      value: AutoridadeEnum.COORDENADOR_FMPS
    },
    {
      label: "Outros",
      value: AutoridadeEnum.OUTROS
    }
  ];

  constructor(
    public fb: FormBuilder,
    public fiscoService: FiscoService,
    private localStorageService: LocalStorageService,
    private cd: ChangeDetectorRef,
  ) {
    super(fb, fiscoService);
    this.initForm();
    this.onRequerenteTipoChange();
    this.handleDisableCheckBox();
    this.setRequestDescription();
    this.formChanges();
  }

  ngOnInit(): void {
    // No ngOnInit, e não no construtor, para o passo da solicitação já receber o formChange.
    this.getFromLocalStorage();
    this.watchFormChanges();
  }

  ngAfterViewInit(): void {
    /* Elemento check box vir selecionado com o tipo do requerente (Servidor, sem rascunho). */
    this.setCheckbox(this.requerenteFormControl.value, true);

    if (this.draftRestored) {
      this.syncEnderecoFormWithDraft();
      // O endereço já tinha sido desenhado vazio: redesenha com o do rascunho.
      this.cd.detectChanges();
    }
  }

  private watchFormChanges(): void {
    this.form.valueChanges.subscribe(() => {
      // O termo de veracidade fica de fora: é aceito no envio.
      const { termoAceito, ...valores } = this.form.getRawValue();
      this.localStorageService.set(this.localStorageKey, valores);
    });
  }

  private getFromLocalStorage(): void {
    const storageData = this.localStorageService.get<any>(this.localStorageKey);

    if (!storageData || typeof storageData !== 'object') return;

    // O tipo primeiro: a troca de tipo limpa os campos do outro tipo de requerente.
    const { SOLS_RequerenteTipo, termoAceito, ...valores } = storageData;

    if (SOLS_RequerenteTipo) {
      this.requerenteFormControl.setValue(SOLS_RequerenteTipo);
    }

    this.form.patchValue(valores);
    this.draftRestored = true;
  }

  private syncEnderecoFormWithDraft(): void {
    this.enderecoFormComponent?.patchInitialValue({
      uf: this.form.get('SOLS_RequerenteUF')?.value,
      localidade: this.form.get('SOLS_RequerenteMunicipio')?.value,
      logradouro: this.form.get('SOLS_RequerenteLogradouro')?.value,
      numero: this.form.get('SOLS_RequerenteNumero')?.value,
      complemento: this.form.get('SOLS_RequerenteComplemento')?.value,
      bairro: this.form.get('SOLS_RequerenteBairro')?.value,
      CEP: this.form.get('SOLS_RequerenteCEP')?.value,
    });
  }

  public setRequerenteTipo(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    this.requerenteFormControl.setValue(checkbox.value);
  }

  public setUFAndStateOnForm(event: { uf: string, estado: string }) {
    this.form.get('SOLS_RequerenteUF').setValue(event.uf);
    this.form.get('SOLS_RequerenteMunicipio').setValue(event.estado);
  }

  public setEndereco(event: { logradouro: string, numero: string, complemento: string, bairro: string, CEP: string, uf: string, localidade: string }) {
    this.form.get('SOLS_RequerenteLogradouro').setValue(event.logradouro);
    this.form.get('SOLS_RequerenteNumero').setValue(event.numero);
    this.form.get('SOLS_RequerenteComplemento').setValue(event.complemento);
    this.form.get('SOLS_RequerenteBairro').setValue(event.bairro);
    this.form.get('SOLS_RequerenteCEP').setValue(event.CEP);
    this.form.get('SOLS_RequerenteUF').setValue(event.uf);
    this.form.get('SOLS_RequerenteMunicipio').setValue(event.localidade);
  }

  public get isServidor(): boolean {
    return this.requerenteFormControl.value === ApplicantSEADTypeEnum.SERVER;
  }

  public get isBeneficiario(): boolean {
    return this.requerenteFormControl.value === ApplicantSEADTypeEnum.RECIPIENT;
  }

  private handleDisableCheckBox() {
    this.requerenteFormControl.valueChanges.subscribe((value) => {
      if (value === ApplicantSEADTypeEnum.SERVER) {
        this.setCheckbox(ApplicantSEADTypeEnum.RECIPIENT, false);
      }

      if (value === ApplicantSEADTypeEnum.RECIPIENT) {
        this.setCheckbox(ApplicantSEADTypeEnum.SERVER, false);
      }
    });
  }

  private initForm() {
    this.form = this.fb.group({
      // Campos gerais
      SOLS_Descricao: ['', Validators.required],
      termoAceito: [null, Validators.requiredTrue],
      SOLS_RequerenteTipo: [ApplicantSEADTypeEnum.SERVER, Validators.required],

      // Campos comuns a ambos os tipos de requerente
      SOLS_RequerenteRG: ['', Validators.required],
      SOLS_RequerenteUF: ['', Validators.required],
      SOLS_RequerenteMunicipio: ['', Validators.required],
      SOLS_RequerenteLogradouro: ['', Validators.required],
      SOLS_RequerenteNumero: ['', Validators.required],
      SOLS_RequerenteBairro: ['', Validators.required],
      SOLS_RequerenteCEP: ['', Validators.required],
      SOLS_RequerenteAutoridadeRequerida: ['', Validators.required],

      // Campos específicos para requerente Servidor
      SOLS_RequerenteRGOrgaoExpeditor: ['', Validators.required],
      SOLS_RequerenteTelefone: ['', Validators.required],
      SOLS_RequerenteDataNascimento: ['', Validators.required],
      SOLS_RequerenteCargo: ['', Validators.required],
      SOLS_RequerenteLotacao: ['', Validators.required],
      SOLS_RequerenteUnidadeExercicio: [''],
      SOLS_RequerenteDataExercicio: [''],
      SOLS_RequerenteComplemento: [''],
      SOLS_RequerenteClasse: [''],

      // Campos específicos para requerente Beneficiário
      SOLS_RequerenteNome: [''],
      SOLS_RequerenteCPF: [''],
      SOLS_RequerenteParentescoExServidor: [''],
      SOLS_RequerenteDataObito: [''],

      formVariants: this.fb.group({
        SOL_Descricao: ["", Validators.required]
      })
    });
  }

  private onRequerenteTipoChange(): void {
    this.requerenteFormControl.valueChanges.subscribe((value) => {
      if (value === ApplicantSEADTypeEnum.SERVER) {
        this.setRequiredAndDesableFields(this.requiredFieldsForServer, this.requiredFieldsForRecipient);
      }

      if (value === ApplicantSEADTypeEnum.RECIPIENT) {
        this.setRequiredAndDesableFields(this.requiredFieldsForRecipient, this.requiredFieldsForServer);
      }
    });
  }

  private setRequiredAndDesableFields(fields: string[], fieldsToDisable: string[]) {
    for (const field of fields) {
      this.form.get(field).setValidators(Validators.required);
    }

    for (const field of fieldsToDisable) {
      this.form.get(field).setValue(null);
      this.form.get(field).clearValidators();
      this.form.get(field).updateValueAndValidity();
    }
  }

  private get requerenteFormControl() {
    return this.form.get('SOLS_RequerenteTipo')
  }

  private setCheckbox(elementId: string, value: boolean) {
    var element = document.getElementById(elementId) as HTMLInputElement;

    // Restaurando o rascunho no ngOnInit, a tela ainda não existe: o ngAfterViewInit marca.
    if (element) element.checked = value;
  }

  private setRequestDescription() {
    this.form.get("SOLS_Descricao").valueChanges.subscribe((response: string) => {
      this.form.get("formVariants").get("SOL_Descricao").patchValue(response)
    })
  }
}
